import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import RecordMetadata from '@/components/RecordMetadata';
import { PicklistConfig } from '@/config/picklists';
import type { AuditProfile } from '@prolarus/shared';

export interface PicklistValue {
  id: string;
  display_value: string;
  storage_value: string;
  sequence: number;
  is_active: boolean;
  is_default: boolean;
  created?: string;
  modified?: string;
  created_by_profile?: AuditProfile | null;
  updated_by_profile?: AuditProfile | null;
}

interface PicklistValueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: PicklistValue | null;
  onSave: (data: Partial<PicklistValue>, keepOpen?: boolean) => Promise<void>;
  isLoading: boolean;
  nextSequence: number;
  picklist: PicklistConfig;
}

export default function PicklistValueDialog({
  open,
  onOpenChange,
  value,
  onSave,
  isLoading,
  nextSequence,
  picklist,
}: PicklistValueDialogProps) {
  const isEditing = !!value?.id;

  const [formData, setFormData] = useState({
    display_value: '',
    storage_value: '',
    sequence: nextSequence,
    is_active: true,
    is_default: false,
  });

  useEffect(() => {
    if (value) {
      setFormData({
        display_value: value.display_value || '',
        storage_value: value.storage_value || '',
        sequence: value.sequence ?? nextSequence,
        is_active: value.is_active ?? true,
        is_default: value.is_default ?? false,
      });
    } else {
      setFormData({
        display_value: '',
        storage_value: '',
        sequence: nextSequence,
        is_active: true,
        is_default: false,
      });
    }
  }, [value, nextSequence]);

  const handleSubmit = async (e: React.FormEvent, keepOpen = false) => {
    e.preventDefault();
    await onSave({
      ...formData,
      id: value?.id,
    }, keepOpen);
  };

  const handleDisplayValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const displayValue = e.target.value;
    setFormData((prev) => ({
      ...prev,
      display_value: displayValue,
      // Auto-generate storage_value from display_value for new records
      storage_value: !isEditing
        ? displayValue
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_|_$/g, '')
        : prev.storage_value,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Picklist Value' : 'Add Picklist Value'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="display_value">Display Value *</Label>
            <Input
              id="display_value"
              value={formData.display_value}
              onChange={handleDisplayValueChange}
              required
              className="mt-1"
              placeholder={`e.g., ${picklist.placeholderExample || 'Value'}`}
            />
          </div>

          <div>
            <Label htmlFor="storage_value">Storage Value *</Label>
            <Input
              id="storage_value"
              value={formData.storage_value}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  storage_value: e.target.value,
                }))
              }
              required
              className="mt-1"
              placeholder={`e.g., ${(picklist.placeholderExample || 'value').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Internal value stored in the database
            </p>
          </div>

          <div>
            <Label htmlFor="sequence">Sequence *</Label>
            <Input
              id="sequence"
              type="number"
              value={formData.sequence}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  sequence: parseInt(e.target.value) || 0,
                }))
              }
              required
              className="mt-1"
              min={1}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Order in which values appear in dropdowns
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="is_active">Active</Label>
              <p className="text-xs text-muted-foreground">
                Inactive values won't appear in dropdowns
              </p>
            </div>
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_active: checked }))
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="is_default">Default</Label>
              <p className="text-xs text-muted-foreground">
                Pre-selected value in dropdowns
              </p>
            </div>
            <Switch
              id="is_default"
              checked={formData.is_default}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_default: checked }))
              }
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            {!isEditing && (
              <Button
                type="button"
                variant="secondary"
                disabled={isLoading}
                onClick={(e) => handleSubmit(e, true)}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save & New'
                )}
              </Button>
            )}
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                isEditing ? 'Save' : 'Save & Close'
              )}
            </Button>
          </div>

          {isEditing && (
            <RecordMetadata record={value} />
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
